// =============================================================================
// Billing Routes  —  /billing
// =============================================================================
// Route registration for the billing & subscription page.
// AuthGuard redirects unauthenticated users to sign-in.
//
// Structurally a near-identical twin of the other simple, single-page
// route files in this directory (profile.tsx, security.tsx) — same
// lazy-load + Suspense + AuthGuard/CONFIG.auth.skip branching shape.
// That's intentional, not boilerplate worth trimming: every dashboard
// page wants identical guard/loading/layout behavior, and matching the
// established shape exactly means anyone who's read one of these has
// effectively read all of them. The `CONFIG.auth.skip` branch exists for
// local development setups that bypass Cognito entirely — when true,
// /billing renders without AuthGuard, which means `get_user_id()` in the
// Lambda would receive an event with no JWT claims and 401 immediately
// (see that function's docstring on the "fails closed" behavior). In
// other words: skipping auth on the FRONTEND route does not — and must
// not — imply the BACKEND trusts the request any more than usual; the
// Lambda-side authorizer is the actual security boundary regardless of
// how this flag is set.
//
// Related files:
//   src/pages/billing.tsx                  → page wrapper
//   src/sections/billing/BillingView.tsx   → all visual content
//   src/layouts/nav-config-account.tsx     → "Subscription" entry in account drawer
// =============================================================================

import type { RouteObject } from 'react-router';

import { lazy, Suspense } from 'react';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { AuthGuard } from 'src/auth/guard';

// ----------------------------------------------------------------------

const BillingPage = lazy(() => import('../../pages/billing'));

// ----------------------------------------------------------------------

export const billingRoutes: RouteObject[] = [
  {
    path: 'billing',
    element: CONFIG.auth.skip ? (
      <Suspense fallback={<LoadingScreen />}>
        <DashboardLayout>
          <BillingPage />
        </DashboardLayout>
      </Suspense>
    ) : (
      <AuthGuard>
        <Suspense fallback={<LoadingScreen />}>
          <DashboardLayout>
            <BillingPage />
          </DashboardLayout>
        </Suspense>
      </AuthGuard>
    ),
  },
];
